import { useEffect, useState } from "react";
import { Loader2, Plus, Send, Trash2, CheckCircle2, XCircle, Sparkles, MessageSquare, Webhook, Zap } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";

const KIND_META = {
  telegram: { icon: Send, label: "Telegram", color: "#229ED9" },
  discord: { icon: MessageSquare, label: "Discord", color: "#5865F2" },
  webhook: { icon: Webhook, label: "Generic webhook", color: "#0055FF" },
};

export default function SocialChannelsPage() {
  const [channels, setChannels] = useState([]);
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("telegram");
  const [name, setName] = useState("");
  const [config, setConfig] = useState({});
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(null);

  const refresh = async () => {
    const [{ data: c }, { data: p }] = await Promise.all([
      api.get("/social/channels"),
      api.get("/social/posts"),
    ]);
    setChannels(c); setPosts(p);
  };

  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    setCreating(true); setError("");
    try {
      await api.post("/social/channels", { kind, name, config });
      setOpen(false);
      setName(""); setConfig({}); setKind("telegram");
      refresh();
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setCreating(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      await api.post(`/social/channels/${id}/test`);
      refresh();
    } catch {}
    finally { setTesting(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Disconnect this channel?")) return;
    await api.delete(`/social/channels/${id}`).catch(() => {});
    refresh();
  };

  const toggle = async (ch) => {
    await api.patch(`/social/channels/${ch.id}`, { enabled: !ch.enabled }).catch(() => {});
    refresh();
  };

  return (
    <DashboardLayout
      eyebrow="Social channels"
      title="Auto-promote your content."
      subtitle="Connect Telegram, Discord, or any Zapier/Make/Buffer webhook. Every published post fans out automatically."
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-10" data-testid="social-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Connect channel
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Connect a social channel</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Channel type</Label>
                <Tabs value={kind} onValueChange={(v) => { setKind(v); setConfig({}); }}>
                  <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm w-full" data-testid="social-kind-tabs">
                    <TabsTrigger value="telegram" className="flex-1 rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="kind-telegram">Telegram</TabsTrigger>
                    <TabsTrigger value="discord" className="flex-1 rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="kind-discord">Discord</TabsTrigger>
                    <TabsTrigger value="webhook" className="flex-1 rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="kind-webhook">Webhook</TabsTrigger>
                  </TabsList>

                  <TabsContent value="telegram" className="mt-4 space-y-3">
                    <CFGInput label="Bot token" placeholder="123456:ABCdef..." onChange={(v) => setConfig((c) => ({ ...c, bot_token: v }))} value={config.bot_token || ""} testid="cfg-tg-token" />
                    <CFGInput label="Chat ID" placeholder="-1001234567890 or @channel" onChange={(v) => setConfig((c) => ({ ...c, chat_id: v }))} value={config.chat_id || ""} testid="cfg-tg-chat" />
                    <Hint>Create a bot via <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-[#3377FF]">@BotFather</a>, paste the token here, and add it to your channel as admin. Use the channel @username or numeric chat ID.</Hint>
                  </TabsContent>

                  <TabsContent value="discord" className="mt-4 space-y-3">
                    <CFGInput label="Webhook URL" placeholder="https://discord.com/api/webhooks/..." onChange={(v) => setConfig((c) => ({ ...c, webhook_url: v }))} value={config.webhook_url || ""} testid="cfg-dc-url" />
                    <Hint>In Discord: <strong>Server Settings → Integrations → Webhooks → New Webhook</strong>. Copy the URL and paste it here.</Hint>
                  </TabsContent>

                  <TabsContent value="webhook" className="mt-4 space-y-3">
                    <CFGInput label="Webhook URL" placeholder="https://hooks.zapier.com/..." onChange={(v) => setConfig((c) => ({ ...c, webhook_url: v }))} value={config.webhook_url || ""} testid="cfg-wh-url" />
                    <Hint>Use Zapier, Make, Buffer, IFTTT or any HTTP webhook. We POST a JSON {"{title,summary,link,image,tags}"} payload — those tools fan out to Twitter, LinkedIn, IG, FB, TikTok, etc.</Hint>
                  </TabsContent>
                </Tabs>
              </div>
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder={`My ${KIND_META[kind].label}`}
                       className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="social-name-input" />
              </div>
              {error && <div className="text-sm text-red-400" data-testid="social-error">{error}</div>}
              <DialogFooter>
                <Button type="submit" disabled={creating} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="social-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {channels.length === 0 ? (
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="social-empty">
          <Zap className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No channels yet</h3>
          <p className="text-zinc-400 max-w-md mx-auto">
            Connect Telegram, Discord, or a webhook to auto-broadcast every blog post to your audience.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="social-channels-grid">
          {channels.map((ch) => {
            const m = KIND_META[ch.kind] || KIND_META.webhook;
            const Icon = m.icon;
            return (
              <div key={ch.id} className={`bg-zinc-950 border rounded-sm p-5 ${ch.enabled ? "border-zinc-800" : "border-zinc-900 opacity-60"}`} data-testid={`channel-${ch.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-sm grid place-items-center" style={{ background: m.color + "15", border: `1px solid ${m.color}40` }}>
                    <Icon className="w-5 h-5" style={{ color: m.color }} />
                  </div>
                  <Switch checked={ch.enabled} onCheckedChange={() => toggle(ch)} data-testid={`channel-toggle-${ch.id}`} />
                </div>
                <div className="font-display text-lg font-medium truncate">{ch.name}</div>
                <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 mt-1 text-[10px] uppercase">{m.label}</Badge>
                {ch.config_masked && (
                  <div className="mt-3 space-y-1">
                    {Object.entries(ch.config_masked).map(([k, v]) => (
                      <div key={k} className="text-[10px] font-mono text-zinc-500 truncate"><span className="text-zinc-600">{k}:</span> {v}</div>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button onClick={() => test(ch.id)} disabled={testing === ch.id} size="sm" variant="outline"
                          className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm text-xs"
                          data-testid={`channel-test-${ch.id}`}>
                    {testing === ch.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1" /> Test</>}
                  </Button>
                  <Button onClick={() => remove(ch.id)} size="sm" variant="ghost"
                          className="text-zinc-500 hover:text-[#EF4444] hover:bg-zinc-900 rounded-sm ml-auto"
                          data-testid={`channel-delete-${ch.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent posts */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="social-posts">
        <div className="px-6 py-4 border-b border-zinc-800">
          <span className="label-eyebrow">Recent broadcasts</span>
        </div>
        {posts.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No broadcasts yet.</div>
        ) : (
          <ul className="divide-y divide-zinc-900">
            {posts.map((p) => (
              <li key={p.id} className="px-6 py-3 flex items-center gap-3" data-testid={`social-post-${p.id}`}>
                {p.ok ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                <span className="text-sm text-zinc-300 truncate flex-1">{p.title || p.kind || "Test message"}</span>
                <Badge variant="outline" className="rounded-sm border-zinc-800 text-zinc-500 text-[10px] uppercase">{p.channel_kind || "—"}</Badge>
                <span className="text-xs text-zinc-600 font-mono">{new Date(p.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
}

function CFGInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF] font-mono text-sm" data-testid={testid} />
    </div>
  );
}

function Hint({ children }) {
  return <p className="text-xs text-zinc-500 leading-relaxed bg-zinc-900 border border-zinc-800 rounded-sm p-3">{children}</p>;
}
